import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { toast } from 'react-toastify';
import {
    FileText, Download, Clock, CheckCircle, XCircle,
    Send, AlertTriangle, Calendar, Info, RefreshCcw
} from 'lucide-react';
import {
    IoClose, IoCheckboxOutline, IoSquareOutline,
    IoFileTrayFullOutline, IoInformationCircleOutline
} from 'react-icons/io5';
import './Offboarding.css';

const EmployeeResignation = ({ user }) => {
    const [resignation, setResignation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [reason, setReason] = useState('');
    const [checklist, setChecklist] = useState([]);

    // Config
    const API_BASE = API_URL;
    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}` } };

    const fetchMyResignation = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/offboarding/my-resignation`, authHeader);
            setResignation(res.data);
            if (res.data && res.data.id) {
                fetchChecklist(res.data.id);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChecklist = async (id) => {
        try {
            const res = await axios.get(`${API_BASE}/offboarding/${id}/checklist`, authHeader);
            setChecklist(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchMyResignation();
    }, []);

    const handleSubmit = async () => {
        if (!reason.trim()) return toast.warning("Please provide a reason.");

        try {
            await axios.post(`${API_BASE}/offboarding/resign`, { reason }, authHeader);
            toast.success("Resignation Submitted Successfully.");
            setShowModal(false);
            fetchMyResignation();
        } catch (error) {
            toast.error(error.response?.data?.error || "Submission Failed");
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("Are you sure you want to cancel your resignation request? We are glad to hear you're staying!")) return;

        try {
            await axios.put(`${API_BASE}/offboarding/${resignation.id}/cancel`, {}, authHeader);
            toast.success("Resignation Cancelled. Glad to have you with us!");
            fetchMyResignation();
        } catch (error) {
            toast.error(error.response?.data?.error || "Cancellation Failed");
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-body)' }}>
            <RefreshCcw className="animate-spin" size={32} color="var(--primary)" />
        </div>
    );

    // 1. No Resignation or Cancelled/Rejected -> Show "Resign" Option
    if (!resignation || resignation.status === 'CANCELLED' || (resignation.status === 'REJECTED' && !resignation.rejection_reason)) {
        return (
            <div className="offboarding-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                <div style={{ textAlign: 'center', maxWidth: 600, width: '100%', padding: '60px 48px', background: '#fff', borderRadius: 32, boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.03)' }}>
                    <div style={{ width: 84, height: 84, background: 'var(--primary-light)', color: 'var(--primary)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 28px', transform: 'rotate(-5deg)' }}>
                        <IoFileTrayFullOutline size={44} />
                    </div>
                    <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#1e293b', marginBottom: 16, letterSpacing: '-0.5px' }}>Resignation & Exit</h2>
                    <p style={{ margin: '0 0 40px 0', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.6, fontWeight: 500 }}>
                        Currently, you have no active departure requests. If you would like to begin the formal exit process, please use the button below to submit your resignation.
                    </p>
                    <button className="btn-primary-premium" style={{ height: 56, padding: '0 40px', fontSize: 16, margin: '0 auto' }} onClick={() => setShowModal(true)}>
                        <Send size={18} /> Initiate Resignation
                    </button>

                    {showModal && (
                        <div className="modal-overlay">
                            <div className="modal-content animate-pop-in" style={{ padding: 40, borderRadius: 28 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                    <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>Submit Resignation</h3>
                                    <button onClick={() => setShowModal(false)} className="close-panel-btn"><IoClose size={22} /></button>
                                </div>
                                <div style={{ marginBottom: 24 }}>
                                    <label className="form-label" style={{ marginBottom: 10, display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)', letterSpacing: '0.05em' }}>REASON FOR LEAVING</label>
                                    <textarea
                                        className="modern-input"
                                        rows="5"
                                        style={{ height: 'auto', padding: 18, width: '100%', borderRadius: 16, background: '#F8FAFC', border: '1px solid #E2E8F0', outline: 'none', fontFamily: 'inherit', fontSize: 14 }}
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        placeholder="Please provide details about your reason for leaving... we appreciate your feedback."
                                    />
                                    <div style={{ marginTop: 16, padding: 14, background: 'var(--primary-light)', borderRadius: 12, fontSize: 13, color: 'var(--primary)', display: 'flex', gap: 10, alignItems: 'center', fontWeight: 500 }}>
                                        <IoInformationCircleOutline size={18} /> Your formal request will be forwarded to the HR department.
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 14 }}>
                                    <button className="btn-terminate" style={{ flex: 1, borderColor: '#E2E8F0', color: '#64748B' }} onClick={() => setShowModal(false)}>Discard</button>
                                    <button className="btn-primary-premium" style={{ flex: 1, boxShadow: 'none' }} onClick={handleSubmit}>Confirm Submission</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // 2. Active Resignation
    return (
        <div className="offboarding-page" style={{ overflowY: 'auto', padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
            <div className="details-panel animate-slide-in" style={{ maxWidth: 840, width: '100%', borderRadius: 32, boxShadow: 'var(--shadow-xl)', overflow: 'visible', padding: '0', height: 'fit-content' }}>

                {/* Header Section */}
                <div style={{ padding: '60px 48px', background: '#fff', borderRadius: '32px' }}>
                    <div className={`corner-badge ${resignation.status.toLowerCase()}`} style={{ top: 40, right: 48, padding: '8px 20px', fontSize: 11 }}>
                        {resignation.status === 'PENDING' ? 'Awaiting HR Approval' : (resignation.status === 'APPROVED' ? 'Cleared & Processing' : 'Offboarding Completed')}
                    </div>

                    <div style={{ paddingBottom: 40, borderBottom: '1px solid #f1f5f9', marginBottom: 40 }}>
                        <h2 style={{ margin: 0, fontSize: '2.2rem', fontWeight: 800, letterSpacing: '-1px' }}>Exit Progress</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, color: 'var(--text-secondary)', fontSize: 15, fontWeight: 600 }}>
                            <Calendar size={18} style={{ color: 'var(--primary)' }} />
                            Request filed on {new Date(resignation.resignation_date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                    </div>

                    <div className="info-grid-premium" style={{ marginBottom: 40, gap: 24 }}>
                        <div className="info-box" style={{ padding: 24, background: '#F8FAFC' }}>
                            <label style={{ letterSpacing: '0.05em' }}>TENTATIVE LAST WORKING DAY</label>
                            <div className="val" style={{ fontSize: 20 }}>{resignation.tentative_lwd ? new Date(resignation.tentative_lwd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</div>
                            <small style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4, display: 'block', fontWeight: 500 }}>Standard notice period apply</small>
                        </div>
                        {resignation.approved_lwd && (
                            <div className="info-box" style={{ padding: 24, background: 'var(--primary-light)', borderColor: 'var(--primary)', borderStyle: 'solid', borderWidth: 1 }}>
                                <label style={{ color: 'var(--primary)', letterSpacing: '0.05em' }}>OFFICIAL LAST WORKING DAY</label>
                                <div className="val" style={{ color: 'var(--primary)', fontSize: 20 }}>{new Date(resignation.approved_lwd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                <small style={{ color: 'var(--primary)', fontSize: 12, marginTop: 4, display: 'block', fontWeight: 600, opacity: 0.8 }}>Finalized by HR Management</small>
                            </div>
                        )}
                    </div>

                    {/* Progress Visual */}
                    <div className="status-card-premium" style={{ marginBottom: 48, background: '#fff', border: '1px solid #E2E8F0', padding: 28 }}>
                        <div className="status-row">
                            <span className="status-label-bold" style={{ fontSize: 15 }}>OVERALL EXIT CLEARANCE</span>
                            <span className="percent-text" style={{ fontSize: 18 }}>
                                {resignation.status === 'COMPLETED' ? '100%' : (resignation.status === 'APPROVED' ? '50%' : '10%')}
                            </span>
                        </div>
                        <div className="prog-track" style={{ height: 12, borderRadius: 20 }}>
                            <div className="prog-fill" style={{ width: resignation.status === 'COMPLETED' ? '100%' : (resignation.status === 'APPROVED' ? '50%' : '10%') }}></div>
                        </div>
                    </div>

                    {/* Checklist */}
                    <div style={{ marginBottom: 48 }}>
                        <h4 style={{ margin: '0 0 24px 0', fontSize: 18, display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800, letterSpacing: '-0.5px' }}>
                            <CheckCircle size={22} color="var(--success)" /> Handover & Clearance Items
                        </h4>
                        {checklist.length > 0 ? (
                            <div className="checklist-container">
                                {checklist.map(item => (
                                    <div key={item.id} className="checklist-card" style={{ cursor: 'default', padding: '18px 24px' }}>
                                        <div className="checkbox-icon">
                                            {item.is_checked ? <CheckCircle size={22} fill="var(--success)" color="#fff" /> : <IoSquareOutline size={22} color="#CBD5E1" />}
                                        </div>
                                        <span className="checklist-text" style={{ fontSize: 15, textDecoration: item.is_checked ? 'line-through' : 'none', opacity: item.is_checked ? 0.6 : 1 }}>
                                            {item.item_name}
                                        </span>
                                        <span className={`status-badge ${item.is_checked ? 'completed' : 'pending'}`} style={{ height: 26, padding: '0 14px' }}>
                                            {item.is_checked ? 'CLEARED' : 'PENDING'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 15, fontStyle: 'italic', textAlign: 'center', padding: '40px 0', background: '#F8FAFC', borderRadius: 20 }}>
                                {resignation.status === 'PENDING' ? 'Your clearance checklist will be generated once HR approves the request.' : 'Loading clearance checklist...'}
                            </div>
                        )}
                    </div>

                    {resignation.status === 'COMPLETED' && resignation.documents && (
                        <div style={{ marginTop: 52 }}>
                            <div className="section-head" style={{ fontSize: 13, marginBottom: 20 }}>Final Exit Documentation</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                                {resignation.documents.map(doc => (
                                    <a key={doc.id} href={doc.document_url} target="_blank" rel="noopener noreferrer" className="doc-card-premium" style={{ padding: 18 }}>
                                        <div className="doc-icon-box" style={{ width: 44, height: 44 }}><FileText size={22} /></div>
                                        <div className="doc-info-main">
                                            <span className="doc-name" style={{ fontSize: 14 }}>{doc.document_type.replace(/_/g, ' ')}</span>
                                            <span className="doc-date">Issued {new Date(doc.generated_at).toLocaleDateString()}</span>
                                        </div>
                                        <Download size={20} color="var(--text-muted)" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {resignation.status === 'REJECTED' && (
                        <div style={{ padding: 28, background: '#fef2f2', borderRadius: 24, border: '1px solid #fee2e2', marginBottom: 40, boxShadow: '0 8px 16px -4px rgba(239, 68, 68, 0.1)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#b91c1c', fontWeight: 800, marginBottom: 12, fontSize: 18 }}>
                                <XCircle size={24} /> Application Rejected
                            </div>
                            <p style={{ margin: 0, color: '#991b1b', fontSize: 16, lineHeight: 1.6, fontWeight: 500 }}>
                                <strong>Rationale:</strong> {resignation.rejection_reason || 'Information not provided. Please connect with your HR representative for detailed feedback.'}
                            </p>
                        </div>
                    )}

                    {/* Cancel Action */}
                    {['PENDING', 'APPROVED'].includes(resignation.status) && (
                        <div style={{ marginTop: 60, borderTop: '1px solid #f1f5f9', paddingTop: 40 }}>
                            <div style={{ background: '--warning-bg', padding: 32, borderRadius: 28, border: '1px solid #FEF3C7', background: '#FFFDF5', display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                                <div style={{ width: 56, height: 56, background: '#FEF3C7', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <AlertTriangle color="#D97706" size={28} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#92400e', fontWeight: 800, fontSize: 18 }}>Changed your mind?</h4>
                                    <p style={{ margin: '0 0 24px 0', fontSize: 15, color: '#b45309', lineHeight: 1.6, fontWeight: 500 }}>
                                        It's not too late to withdraw your resignation. If you've decided to continue your journey with us, you can cancel your request here.
                                    </p>
                                    <button className="btn-terminate" onClick={handleCancel} style={{ background: '#fff', width: 'fit-content', padding: '0 24px', borderColor: '#F59E0B', color: '#B45309' }}>
                                        Withdraw Resignation
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeResignation;
